import { format, isFuture } from "date-fns";
import { Clock, User, MapPin, FileText, Pencil } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { useCompanySettings, isSwapsEnabled } from "@/hooks/useCompanySettings";
import { useHasPermission } from "@/hooks/permissions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
  DialogPortal,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface Shift {
  id: string;
  profile_id: string | null;
  start_time: string;
  end_time: string;
  note: string | null;
  employee_name: string;
  status?: 'active' | 'open';
  offered_by_user_id?: string | null;
  offered_by_name?: string | null;
}

interface ShiftDetailsDialogProps {
  shift: Shift | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCantWork?: (shift: Shift) => void;
  onEditShift?: (shift: Shift) => void;
}

export function ShiftDetailsDialog({ 
  shift, 
  open, 
  onOpenChange, 
  onCantWork,
  onEditShift
}: ShiftDetailsDialogProps) {
  const isMobile = useIsMobile();
  const { profile } = useAuth();
  const { settings: companySettings } = useCompanySettings();
  
  // Permission check for editing own shift times
  const canChangeOwnShiftTimes = useHasPermission('change_own_shift_times');

  if (!shift) return null;

  // Check if this is the user's own shift and it's in the future
  const isOwnShift = shift.profile_id === profile?.id;
  const isFutureShift = isFuture(new Date(shift.start_time));
  const canRequestSwap = isOwnShift && isFutureShift && isSwapsEnabled(companySettings);
  
  // Can edit own shift times if: has permission, is own shift, and shift is in the future
  const canEditShift = canChangeOwnShiftTimes && isOwnShift && isFutureShift;

  // Calculate shift duration
  const startTime = new Date(shift.start_time);
  const endTime = new Date(shift.end_time);
  const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

  // Get shift status display
  const getShiftStatusInfo = () => {
    if (shift.status === 'open' || !shift.profile_id) {
      if (shift.offered_by_user_id && shift.offered_by_name) {
        return {
          label: `Offered by ${shift.offered_by_name}`,
          variant: "secondary" as const,
          className: "bg-orange-50 text-orange-700 border-orange-300"
        };
      } else {
        return {
          label: "Open shift",
          variant: "outline" as const,
          className: "bg-muted/40 text-muted-foreground"
        };
      }
    } else {
      return {
        label: "Assigned",
        variant: "default" as const,
        className: "bg-primary/10 text-primary"
      };
    }
  };

  const statusInfo = getShiftStatusInfo();

  const handleCantWork = () => {
    if (onCantWork && shift) {
      onCantWork(shift);
    }
  };

  const handleEditShift = () => {
    if (onEditShift && shift) {
      onEditShift(shift);
      onOpenChange(false);
    }
  };

  // Shared content component
  const shiftContent = (
    <div className="space-y-6">
      {/* Shift Status */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Shift Details</h3>
        <Badge variant={statusInfo.variant} className={statusInfo.className}>
          {statusInfo.label}
        </Badge>
      </div>

      {/* Date and Time */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="font-medium">
              {format(startTime, 'EEEE, MMMM d, yyyy')}
            </div>
            <div className="text-sm text-muted-foreground">
              {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
              <span className="ml-2">({durationHours.toFixed(1)} hours)</span>
            </div>
          </div>
        </div>

        {/* Employee */}
        <div className="flex items-center gap-3">
          <User className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="font-medium">Employee</div>
            <div className="text-sm text-muted-foreground">
              {shift.employee_name}
            </div>
          </div>
        </div>

        {/* Notes */}
        {shift.note && (
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <div className="font-medium">Notes</div>
              <div className="text-sm text-muted-foreground">
                {shift.note}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Swap Information */}
      {canRequestSwap && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-sm text-blue-800">
            <strong>Can't work this shift?</strong>
            <p className="mt-1">
              You can request to swap this shift with a coworker who has a future shift.
            </p>
          </div>
        </div>
      )}
    </div>
  );

  // Shared footer component
  const shiftFooter = (
    <div className="flex gap-2 justify-end">
      <Button
        variant="outline"
        onClick={() => onOpenChange(false)}
      >
        Close
      </Button>
      {canEditShift && onEditShift && (
        <Button
          variant="outline"
          onClick={handleEditShift}
        >
          <Pencil className="h-4 w-4 mr-2" />
          Edit Times
        </Button>
      )}
      {canRequestSwap && (
        <Button
          onClick={handleCantWork}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          Can't Work
        </Button>
      )}
    </div>
  );

  if (isMobile) {
    // Mobile: Use Drawer
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="z-[60]">
          <DrawerHeader>
            <DrawerTitle>Shift Details</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-4">
            {shiftContent}
          </div>
          <DrawerFooter className="px-4">
            <div className="flex flex-col gap-2">
              {canRequestSwap && (
                <Button
                  onClick={handleCantWork}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Can't Work
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Use Dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
        <DialogOverlay className="fixed inset-0 z-[60] bg-black/50" />
        <DialogContent className="z-[60] sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Shift Details</DialogTitle>
          </DialogHeader>
          {shiftContent}
          <DialogFooter>
            {shiftFooter}
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
